import React from 'react';
import { motion } from 'framer-motion';
import { 
  Gamepad2, Video, Upload, FileCheck, Award, Coins,
  Sun, Camera, Hand, Clock, EyeOff, Scissors, Check, X
} from 'lucide-react';

const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      icon: Gamepad2,
      title: 'CHOOSE A SIDE QUEST',
      description: 'Browse available quests and pick one that matches your skills or environment.',
      color: 'bg-neon-pink',
    },
    {
      number: '02',
      icon: Video,
      title: 'RECORD CLEAR VIDEO DATA',
      description: 'Capture stable, well-lit footage showing the complete task from start to finish.',
      color: 'bg-trophy-gold',
    },
    {
      number: '03',
      icon: Upload,
      title: 'UPLOAD DIRECTLY',
      description: 'Log in to your account and upload your video directly through our platform.',
      color: 'bg-success-green',
    },
    {
      number: '04',
      icon: FileCheck,
      title: 'SUBMIT FOR REVIEW',
      description: 'Complete the submission form with your device information and details.',
      color: 'bg-lavender',
    },
    {
      number: '05',
      icon: Award,
      title: 'REVIEW & APPROVAL',
      description: 'Our team and partners review your submission for quality and compliance.',
      color: 'bg-warning-orange',
    },
    {
      number: '06',
      icon: Coins,
      title: 'GET RANKED & PAID',
      description: 'Approved submissions count toward leaderboards and payouts.',
      color: 'bg-neon-pink',
    },
  ];

  const dataStandards = {
    dos: [
      { icon: Camera, text: 'Stable camera position' },
      { icon: Sun, text: 'Good lighting conditions' },
      { icon: Hand, text: 'Show hands, tools & objects clearly' },
      { icon: Clock, text: 'Full task from start to finish' },
    ],
    donts: [
      { icon: EyeOff, text: 'Avoid showing faces' },
      { icon: Scissors, text: 'No heavy edits or cuts' },
      { icon: X, text: 'No private/sensitive info visible' },
    ],
  };

  return (
    <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-deep-purple/50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-1 bg-trophy-gold" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-trophy-gold">
              HOW IT WORKS
            </h2>
            <div className="w-12 h-1 bg-trophy-gold" />
          </div>
          <p className="font-terminal text-xl text-lavender">
            Six simple steps to start earning
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="arcade-panel rounded-lg p-6 relative overflow-hidden group"
            >
              {/* Step number */}
              <div className="absolute top-2 right-2 font-pixel text-4xl text-panel-purple/50">
                {step.number}
              </div>
              
              {/* Icon */}
              <div className={`w-14 h-14 ${step.color} rounded flex items-center justify-center mb-4 border-2 border-dark-border shadow-arcade-sm group-hover:scale-110 transition-transform`}>
                <step.icon className="w-7 h-7 text-dark-border" />
              </div>
              
              {/* Content */}
              <h3 className="font-pixel text-[10px] sm:text-xs text-foreground mb-3">
                {step.title}
              </h3>
              <p className="font-terminal text-lg text-lavender">
                {step.description}
              </p>

              {/* Connection line (except last in row) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-neon-pink/50" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Data Standards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg overflow-hidden"
        >
          <div className="bg-neon-pink/20 px-6 py-4 border-b-4 border-dark-border">
            <h3 className="font-pixel text-sm sm:text-base text-neon-pink text-center">
              📹 CLEAR DATA STANDARDS
            </h3>
          </div>
          
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Do's */}
              <div>
                <h4 className="font-pixel text-xs text-success-green mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  DO THIS
                </h4>
                <div className="space-y-3">
                  {dataStandards.dos.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 p-3 bg-success-green/10 border-l-4 border-success-green rounded"
                    >
                      <item.icon className="w-5 h-5 text-success-green flex-shrink-0" />
                      <span className="font-terminal text-lg text-foreground">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Don'ts */}
              <div>
                <h4 className="font-pixel text-xs text-warning-orange mb-4 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  AVOID THIS
                </h4>
                <div className="space-y-3">
                  {dataStandards.donts.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 p-3 bg-warning-orange/10 border-l-4 border-warning-orange rounded"
                    >
                      <item.icon className="w-5 h-5 text-warning-orange flex-shrink-0" />
                      <span className="font-terminal text-lg text-foreground">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
